import { useEffect, useState } from "react";
import { Plus, Trash2, BellRing, Mail, CheckCircle2, Edit3, Pause, Play, Send, AlertTriangle, Clock, Sparkles, X } from "lucide-react";
import { api } from "@/lib/apiClient";
import PageHeader from "@/components/PageHeader";
import { nyFormat } from "@/lib/time";

const KINDS = [
    { value: "chart_review", label: "Chart review" },
    { value: "session", label: "Session" },
    { value: "economic_event", label: "Economic event" },
    { value: "weekend_backtest", label: "Weekend backtest" },
    { value: "weekly_review", label: "Weekly review" },
    { value: "custom", label: "Custom" },
];

const WEEKDAYS = [
    { v: 0, l: "Sun" },
    { v: 1, l: "Mon" },
    { v: 2, l: "Tue" },
    { v: 3, l: "Wed" },
    { v: 4, l: "Thu" },
    { v: 5, l: "Fri" },
    { v: 6, l: "Sat" },
];

const EMPTY = {
    kind: "chart_review",
    title: "",
    repeat: "none",
    scheduled_at: "",
    local_hhmm: "07:00",
    repeat_days: [1, 2, 3, 4, 5],
    enabled: true,
    email: false,
};

export default function Reminders() {
    const [list, setList] = useState([]);
    const [form, setForm] = useState(EMPTY);
    const [editingId, setEditingId] = useState(null);
    const [open, setOpen] = useState(false);
    const [status, setStatus] = useState({});
    const [templates, setTemplates] = useState([]);
    const [preview, setPreview] = useState(null);  // { template, overrides } or null
    const [previewError, setPreviewError] = useState("");
    const [previewBusy, setPreviewBusy] = useState(false);

    const load = async () => {
        const [l, t] = await Promise.all([api.get("/reminders"), api.get("/reminders/templates")]);
        setList(l.data.sort((a, b) => (a.next_run_at || "9").localeCompare(b.next_run_at || "9")));
        setTemplates(t.data);
    };
    useEffect(() => { load(); }, []);

    const openPreview = (t) => {
        setPreviewError("");
        setPreview({
            template: t,
            overrides: {
                title: t.title,
                local_hhmm: t.local_hhmm,
                repeat: t.repeat,
                repeat_days: [...t.repeat_days],
                email: t.email,
                enabled: true,
            },
        });
    };

    const closePreview = () => { setPreview(null); setPreviewError(""); };

    const savePreset = async () => {
        if (!preview) return;
        setPreviewBusy(true);
        setPreviewError("");
        try {
            await api.post("/reminders/from-template", {
                template_id: preview.template.template_id,
                ...preview.overrides,
            });
            setPreview(null);
            load();
        } catch (e) {
            const detail = e.response?.data?.detail;
            if (detail && typeof detail === "object" && detail.error === "duplicate_template") {
                setPreviewError(detail.message || "This template already exists.");
            } else if (typeof detail === "string") {
                setPreviewError(detail);
            } else {
                setPreviewError("Could not save the preset. Please try again.");
            }
        } finally {
            setPreviewBusy(false);
        }
    };

    const save = async (e) => {
        e.preventDefault();
        if (!form.title) return;
        const payload = { ...form };
        if (form.repeat === "none") {
            if (!form.scheduled_at) return;
            payload.scheduled_at = new Date(form.scheduled_at).toISOString();
            payload.local_hhmm = null;
            payload.repeat_days = [];
        } else {
            payload.scheduled_at = null;
            if (form.repeat === "daily") payload.repeat_days = [];
        }
        if (editingId) {
            await api.put(`/reminders/${editingId}`, payload);
        } else {
            await api.post("/reminders", payload);
        }
        setForm(EMPTY);
        setEditingId(null);
        setOpen(false);
        load();
    };

    const edit = (r) => {
        setForm({
            kind: r.kind,
            title: r.title,
            repeat: r.repeat || "none",
            scheduled_at: r.scheduled_at ? new Date(r.scheduled_at).toISOString().slice(0, 16) : "",
            local_hhmm: r.local_hhmm || "07:00",
            repeat_days: r.repeat_days || [],
            enabled: r.enabled ?? true,
            email: r.email ?? false,
        });
        setEditingId(r.id);
        setOpen(true);
    };

    const togglePause = async (r) => {
        await api.put(`/reminders/${r.id}`, { enabled: !r.enabled });
        load();
    };
    const del = async (id) => { await api.delete(`/reminders/${id}`); load(); };
    const markSeen = async (id) => { await api.post(`/reminders/${id}/mark-seen`); load(); };
    const testSend = async (id) => {
        setStatus({ ...status, [id]: "sending" });
        try {
            const { data } = await api.post(`/reminders/${id}/test`);
            setStatus({ ...status, [id]: data.status });
        } catch {
            setStatus({ ...status, [id]: "failed" });
        }
        load();
    };

    const toggleDay = (d) => {
        const has = form.repeat_days.includes(d);
        setForm({ ...form, repeat_days: has ? form.repeat_days.filter((x) => x !== d) : [...form.repeat_days, d].sort() });
    };

    const now = new Date();
    const upcoming = list.filter((r) => r.enabled && r.next_run_at && new Date(r.next_run_at) >= now);
    const past = list.filter((r) => !(r.enabled && r.next_run_at && new Date(r.next_run_at) >= now));

    return (
        <div className="space-y-8" data-testid="reminders-page">
            <PageHeader
                eyebrow="Prompts, not interruptions"
                title="Reminders"
                subtitle="Set one-time or recurring prompts to support your trading routine, reviews, and goals."
                action={
                    <button onClick={() => { setForm(EMPTY); setEditingId(null); setOpen(!open); }} className="bg-[#D4AF37] text-[#0E0E10] px-3 py-2 rounded-md hover:bg-[#B3922E] text-sm inline-flex items-center gap-2" data-testid="rem-new-btn">
                        <Plus className="w-4 h-4" /> {editingId ? "Cancel edit" : "New reminder"}
                    </button>
                }
            />

            <section data-testid="templates-section">
                <div className="caption mb-3 flex items-center gap-2"><Sparkles className="w-3 h-3 gold" /> One-tap presets</div>
                <div className="grid md:grid-cols-2 gap-3">
                    {templates.map((t) => {
                        const created = Boolean(t.already_created_id);
                        return (
                            <div
                                key={t.template_id}
                                className={`twp-card p-5 flex items-start gap-4 ${created ? "opacity-80" : ""}`}
                                data-testid={`template-${t.template_id}`}
                            >
                                <div className="h-9 w-9 shrink-0 rounded-md bg-[#D4AF37]/10 border border-[#D4AF37]/30 flex items-center justify-center">
                                    <BellRing className="w-4 h-4 gold" strokeWidth={1.5} />
                                </div>
                                <div className="flex-1 min-w-0">
                                    <div className="font-serif text-base">{t.label}</div>
                                    <p className="text-xs text-zinc-500 mt-1 leading-relaxed">{t.description}</p>
                                    {created ? (
                                        <div className="mt-3 flex items-center gap-3 flex-wrap">
                                            <span className="text-[10px] px-1.5 py-0.5 rounded bg-[#5B8A62]/15 text-[#9BC7A2] border border-[#5B8A62]/40 uppercase tracking-wider">
                                                Added
                                            </span>
                                            {t.already_enabled === false && (
                                                <span className="text-[10px] text-zinc-400 uppercase tracking-wider">paused</span>
                                            )}
                                            {t.already_next_run_at && (
                                                <span className="text-xs text-zinc-500 font-mono">next {nyFormat(t.already_next_run_at)} ET</span>
                                            )}
                                        </div>
                                    ) : (
                                        <button
                                            onClick={() => openPreview(t)}
                                            data-testid={`template-add-${t.template_id}`}
                                            className="mt-3 inline-flex items-center gap-1.5 text-xs text-[#D4AF37] hover:underline"
                                        >
                                            <Plus className="w-3 h-3" /> Preview & add
                                        </button>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </div>
            </section>

            {preview && (
                <TemplatePreview
                    preview={preview}
                    setOverrides={(o) => setPreview({ ...preview, overrides: { ...preview.overrides, ...o } })}
                    onCancel={closePreview}
                    onSave={savePreset}
                    error={previewError}
                    busy={previewBusy}
                />
            )}

            {open && (
                <form onSubmit={save} className="twp-card p-6 space-y-4" data-testid="rem-form">
                    <div className="grid md:grid-cols-2 gap-3">
                        <select value={form.kind} onChange={(e) => setForm({ ...form, kind: e.target.value })} className="bg-[#0e0e10] border border-white/10 rounded-md px-3 py-2 text-sm" data-testid="rem-kind">
                            {KINDS.map((k) => <option key={k.value} value={k.value}>{k.label}</option>)}
                        </select>
                        <input placeholder="Title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} required className="bg-[#0e0e10] border border-white/10 rounded-md px-3 py-2 text-sm" data-testid="rem-title" />
                    </div>

                    <div className="flex gap-2 flex-wrap" data-testid="rem-repeat-tabs">
                        {[
                            { v: "none", l: "One-time" },
                            { v: "daily", l: "Daily" },
                            { v: "weekly", l: "Weekly" },
                        ].map((r) => (
                            <button key={r.v} type="button" onClick={() => setForm({ ...form, repeat: r.v })}
                                data-testid={`rem-repeat-${r.v}`}
                                className={`px-3 py-1.5 rounded-md text-xs transition-colors ${form.repeat === r.v ? "bg-[#D4AF37] text-[#0E0E10]" : "text-zinc-400 border border-white/10 hover:border-white/20"}`}>
                                {r.l}
                            </button>
                        ))}
                    </div>

                    {form.repeat === "none" && (
                        <input type="datetime-local" value={form.scheduled_at} onChange={(e) => setForm({ ...form, scheduled_at: e.target.value })} required className="w-full bg-[#0e0e10] border border-white/10 rounded-md px-3 py-2 text-sm" data-testid="rem-time" />
                    )}

                    {(form.repeat === "daily" || form.repeat === "weekly") && (
                        <div className="flex items-center gap-3">
                            <label className="caption">Time (America/New_York)</label>
                            <input type="time" value={form.local_hhmm} onChange={(e) => setForm({ ...form, local_hhmm: e.target.value })} required className="bg-[#0e0e10] border border-white/10 rounded-md px-3 py-2 text-sm" data-testid="rem-local-time" />
                        </div>
                    )}

                    {form.repeat === "weekly" && (
                        <div>
                            <div className="caption mb-2">Fires on</div>
                            <div className="flex gap-2 flex-wrap">
                                {WEEKDAYS.map((d) => (
                                    <button key={d.v} type="button" onClick={() => toggleDay(d.v)} data-testid={`rem-dow-${d.v}`}
                                        className={`w-10 h-10 rounded-md text-xs transition-colors ${form.repeat_days.includes(d.v) ? "bg-[#D4AF37] text-[#0E0E10]" : "text-zinc-400 border border-white/10 hover:border-white/20"}`}>
                                        {d.l}
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}

                    <div className="grid md:grid-cols-2 gap-3">
                        <label className="flex items-center gap-2 text-sm text-zinc-400">
                            <input type="checkbox" checked={form.enabled} onChange={(e) => setForm({ ...form, enabled: e.target.checked })} className="accent-[#D4AF37]" data-testid="rem-enabled" />
                            Enabled
                        </label>
                        <label className="flex items-center gap-2 text-sm text-zinc-400">
                            <input type="checkbox" checked={form.email} onChange={(e) => setForm({ ...form, email: e.target.checked })} className="accent-[#D4AF37]" data-testid="rem-email" />
                            Send email at scheduled time
                        </label>
                    </div>

                    <button type="submit" className="bg-[#D4AF37] text-[#0E0E10] px-4 py-2 rounded-md hover:bg-[#B3922E] text-sm" data-testid="rem-save">
                        {editingId ? "Save changes" : "Create reminder"}
                    </button>
                </form>
            )}

            <ReminderList title="Active" items={upcoming} testid="active" onEdit={edit} onPause={togglePause} onDel={del} onSeen={markSeen} onTest={testSend} status={status} />
            <ReminderList title="Paused / completed" items={past} testid="past" muted onEdit={edit} onPause={togglePause} onDel={del} onSeen={markSeen} onTest={testSend} status={status} />

        </div>
    );
}

function ReminderList({ title, items, testid, onEdit, onPause, onDel, onSeen, onTest, status, muted }) {
    return (
        <section data-testid={`rem-section-${testid}`}>
            <div className="caption mb-3">{title}</div>
            {items.length === 0 && <div className="text-sm text-zinc-500">None.</div>}
            <div className="space-y-2">
                {items.map((r) => (
                    <div key={r.id} className={`twp-card p-4 ${muted ? "opacity-70" : ""}`} data-testid={`rem-${r.id}`}>
                        <div className="flex items-start gap-4 flex-wrap">
                            <div className="shrink-0">
                                <BellRing className="w-4 h-4 gold" strokeWidth={1.5} />
                                <div className="caption mt-1 whitespace-nowrap">{(r.kind || "custom").replace("_", " ")}</div>
                            </div>
                            <div className="flex-1 min-w-0">
                                <div className="text-sm flex items-center gap-2 flex-wrap">
                                    <span>{r.title}</span>
                                    <RepeatBadge r={r} />
                                    {!r.enabled && <span className="text-[10px] px-1.5 py-0.5 rounded bg-zinc-700/40 text-zinc-300 uppercase tracking-wider">Paused</span>}
                                </div>
                                <div className="caption mt-1 font-mono flex flex-wrap gap-x-4 gap-y-1">
                                    {r.next_run_at && <span>Next: {nyFormat(r.next_run_at)} ET</span>}
                                    {r.last_run_at && <span>Last: {nyFormat(r.last_run_at)} ET</span>}
                                </div>
                                <DeliveryBadge r={r} localStatus={status[r.id]} />
                            </div>
                            <div className="flex items-center gap-1 shrink-0">
                                <button onClick={() => onPause(r)} className="p-2 rounded hover:bg-white/5 text-zinc-400 hover:text-[#D4AF37]" title={r.enabled ? "Pause" : "Resume"} data-testid={`rem-pause-${r.id}`}>
                                    {r.enabled ? <Pause className="w-4 h-4" strokeWidth={1.5} /> : <Play className="w-4 h-4" strokeWidth={1.5} />}
                                </button>
                                <button onClick={() => onEdit(r)} className="p-2 rounded hover:bg-white/5 text-zinc-400" data-testid={`rem-edit-${r.id}`}>
                                    <Edit3 className="w-4 h-4" strokeWidth={1.5} />
                                </button>
                                <button onClick={() => onSeen(r.id)} className="p-2 rounded hover:bg-white/5 text-zinc-400" title="Mark in-app seen" data-testid={`rem-seen-${r.id}`}>
                                    <CheckCircle2 className="w-4 h-4" strokeWidth={1.5} />
                                </button>
                                <button onClick={() => onTest(r.id)} className="p-2 rounded hover:bg-white/5 text-zinc-400 hover:text-[#D4AF37]" title="Send test email now" data-testid={`rem-test-${r.id}`}>
                                    <Send className="w-4 h-4" strokeWidth={1.5} />
                                </button>
                                <button onClick={() => onDel(r.id)} className="p-2 rounded text-zinc-500 hover:text-[#B95050]" data-testid={`rem-del-${r.id}`}>
                                    <Trash2 className="w-4 h-4" strokeWidth={1.5} />
                                </button>
                            </div>
                        </div>
                    </div>
                ))}
            </div>
        </section>
    );
}

function RepeatBadge({ r }) {
    if (r.repeat === "daily") return <span className="text-[10px] px-1.5 py-0.5 rounded bg-[#D4AF37]/10 text-[#D4AF37] border border-[#D4AF37]/30 uppercase tracking-wider">Daily · {r.local_hhmm}</span>;
    if (r.repeat === "weekly") {
        const days = (r.repeat_days || []).map((d) => ["Sun","Mon","Tue","Wed","Thu","Fri","Sat"][d]).join(" ");
        return <span className="text-[10px] px-1.5 py-0.5 rounded bg-[#D4AF37]/10 text-[#D4AF37] border border-[#D4AF37]/30 uppercase tracking-wider">Wk · {days} · {r.local_hhmm}</span>;
    }
    return <span className="text-[10px] px-1.5 py-0.5 rounded bg-zinc-700/30 text-zinc-400 border border-white/5 uppercase tracking-wider">One-time</span>;
}

function DeliveryBadge({ r, localStatus }) {
    const items = [];
    if (r.delivered_in_app) items.push(<span key="s" className="text-[#5B8A62] flex items-center gap-1"><CheckCircle2 className="w-3 h-3" /> seen</span>);
    if (r.email && r.delivery_status) {
        const s = r.delivery_status;
        if (s === "sent") items.push(<span key="e" className="text-[#5B8A62] flex items-center gap-1"><Mail className="w-3 h-3" /> sent</span>);
        else if (s === "failed") items.push(<span key="e" className="text-[#B95050] flex items-center gap-1"><Mail className="w-3 h-3" /> failed</span>);
        else if (s === "missed") items.push(<span key="e" className="text-amber-300 flex items-center gap-1"><AlertTriangle className="w-3 h-3" /> missed</span>);
        else if (s === "queued") items.push(<span key="e" className="text-zinc-400 flex items-center gap-1"><Clock className="w-3 h-3" /> queued</span>);
        else if (s === "sending") items.push(<span key="e" className="text-amber-300 flex items-center gap-1"><Clock className="w-3 h-3" /> sending</span>);
    }
    if (!r.email) items.push(<span key="noemail" className="text-zinc-500 flex items-center gap-1"><Mail className="w-3 h-3" /> email off</span>);
    if (localStatus === "sending") items.push(<span key="t" className="text-zinc-500">test sending…</span>);
    if (r.last_error) items.push(<span key="err" className="text-[#B95050] truncate max-w-sm" title={r.last_error}>· {r.last_error}</span>);
    if (!items.length) return null;
    return <div className="mt-2 flex items-center gap-3 flex-wrap text-xs" data-testid={`rem-status-${r.id}`}>{items}</div>;
}

function TemplatePreview({ preview, setOverrides, onCancel, onSave, error, busy }) {
    const { template, overrides } = preview;
    const toggleDay = (d) => {
        const has = overrides.repeat_days.includes(d);
        setOverrides({ repeat_days: has ? overrides.repeat_days.filter((x) => x !== d) : [...overrides.repeat_days, d].sort() });
    };
    return (
        <div className="fixed inset-0 z-50 flex items-start md:items-center justify-center p-4 overflow-y-auto bg-black/70 backdrop-blur-sm" data-testid="template-preview">
            <div className="w-full max-w-xl bg-[#18181B] border border-white/10 rounded-lg shadow-[0_20px_60px_rgba(0,0,0,0.6)] my-6">
                <div className="flex items-start justify-between px-6 pt-5 pb-3 border-b border-white/5">
                    <div>
                        <div className="caption">Preset preview</div>
                        <h3 className="font-serif text-xl mt-1">{template.label}</h3>
                    </div>
                    <button onClick={onCancel} className="text-zinc-400 hover:text-white p-1" aria-label="Close" data-testid="preview-close">
                        <X className="w-4 h-4" />
                    </button>
                </div>

                <div className="px-6 py-5 space-y-4">
                    <p className="text-sm text-zinc-400 leading-relaxed">{template.description}</p>

                    <div>
                        <label className="caption">Title</label>
                        <input
                            value={overrides.title}
                            onChange={(e) => setOverrides({ title: e.target.value })}
                            className="mt-2 w-full bg-[#0e0e10] border border-white/10 rounded-md px-3 py-2 text-sm"
                            data-testid="preview-title"
                        />
                    </div>

                    <div>
                        <label className="caption">Message</label>
                        <div className="mt-2 px-3 py-2 bg-[#0e0e10] border border-white/5 rounded-md text-xs text-zinc-400 leading-relaxed italic">
                            {template.message}
                        </div>
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <label className="caption">Time (ET)</label>
                            <input
                                type="time"
                                value={overrides.local_hhmm}
                                onChange={(e) => setOverrides({ local_hhmm: e.target.value })}
                                className="mt-2 w-full bg-[#0e0e10] border border-white/10 rounded-md px-3 py-2 text-sm"
                                data-testid="preview-time"
                            />
                        </div>
                        <div>
                            <label className="caption">Recurrence</label>
                            <select
                                value={overrides.repeat}
                                onChange={(e) => setOverrides({ repeat: e.target.value })}
                                className="mt-2 w-full bg-[#0e0e10] border border-white/10 rounded-md px-3 py-2 text-sm"
                                data-testid="preview-repeat"
                            >
                                <option value="daily">Daily</option>
                                <option value="weekly">Weekly</option>
                            </select>
                        </div>
                    </div>

                    {overrides.repeat === "weekly" && (
                        <div>
                            <div className="caption mb-2">Fires on</div>
                            <div className="flex gap-2 flex-wrap">
                                {WEEKDAYS.map((d) => (
                                    <button
                                        key={d.v}
                                        type="button"
                                        onClick={() => toggleDay(d.v)}
                                        data-testid={`preview-dow-${d.v}`}
                                        className={`w-10 h-10 rounded-md text-xs transition-colors ${overrides.repeat_days.includes(d.v) ? "bg-[#D4AF37] text-[#0E0E10]" : "text-zinc-400 border border-white/10 hover:border-white/20"}`}
                                    >
                                        {d.l}
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}

                    <div className="grid grid-cols-2 gap-3">
                        <label className="flex items-center gap-2 text-sm text-zinc-400">
                            <input
                                type="checkbox"
                                checked={overrides.enabled}
                                onChange={(e) => setOverrides({ enabled: e.target.checked })}
                                className="accent-[#D4AF37]"
                                data-testid="preview-enabled"
                            />
                            Enabled
                        </label>
                        <label className="flex items-center gap-2 text-sm text-zinc-400">
                            <input
                                type="checkbox"
                                checked={overrides.email}
                                onChange={(e) => setOverrides({ email: e.target.checked })}
                                className="accent-[#D4AF37]"
                                data-testid="preview-email"
                            />
                            Send email
                        </label>
                    </div>

                    {error && (
                        <div className="px-3 py-2 bg-[#B95050]/10 border border-[#B95050]/30 rounded-md text-sm text-[#E8A5A5]" data-testid="preview-error">
                            {error}
                        </div>
                    )}
                </div>

                <div className="flex items-center justify-end gap-2 px-6 py-4 border-t border-white/5">
                    <button
                        onClick={onCancel}
                        className="px-3 py-2 text-sm text-zinc-300 hover:text-white"
                        data-testid="preview-cancel"
                    >
                        Cancel
                    </button>
                    <button
                        onClick={onSave}
                        disabled={busy || !overrides.title || (overrides.repeat === "weekly" && overrides.repeat_days.length === 0)}
                        className="bg-[#D4AF37] text-[#0E0E10] px-4 py-2 rounded-md hover:bg-[#B3922E] text-sm disabled:opacity-50"
                        data-testid="preview-save"
                    >
                        {busy ? "Saving…" : "Save preset"}
                    </button>
                </div>
            </div>
        </div>
    );
}

